/**
 * Carregamento dos dados das estações e revalidação periódica.
 *
 * Os arquivos só são baixados quando a camada é ligada pela primeira vez —
 * quem nunca abre a aba Estações não paga nada. Depois disso, o gancho de
 * atualização automática do loop revalida a cada ciclo, porque o pipeline
 * publica de 10 em 10 minutos.
 */
import { carregarDados } from './api';
import { frescorDe } from './frescor';
import { useStationStore } from './store';

let emVoo: AbortController | null = null;
let jaCarregou = false;

/** Baixa cadastro + grade. `force` revalida mesmo já tendo dados. */
export async function carregarEstacoes(force = false): Promise<void> {
  const { situacao } = useStationStore.getState();
  if (!force && (situacao.estado === 'loading' || jaCarregou)) return;

  emVoo?.abort();
  const ctrl = new AbortController();
  emVoo = ctrl;

  useStationStore.getState().setSituacao({
    estado: 'loading',
    mensagem: 'Baixando as estações…',
    erro: null,
  });

  try {
    const dados = await carregarDados(ctrl.signal);
    if (ctrl.signal.aborted) return;
    jaCarregou = true;
    useStationStore.getState().setDados(dados);
    useStationStore.getState().setSituacao({
      estado: 'ready',
      mensagem: dados.grade ? null : 'Grade indisponível — só o cadastro das estações foi lido.',
      erro: null,
    });
  } catch (e) {
    if (ctrl.signal.aborted) return;
    useStationStore.getState().setSituacao({
      estado: 'error',
      mensagem: null,
      erro: e instanceof Error ? e.message : String(e),
    });
  } finally {
    if (emVoo === ctrl) emVoo = null;
  }
}

/** Revalidação do ciclo automático — só se a camada estiver em uso. */
export function refreshEstacoes(): void {
  if (!jaCarregou || !useStationStore.getState().visivel) return;
  void carregarEstacoes(true);
}

/**
 * Liga o carregamento sob demanda: a primeira vez que a camada é ligada,
 * baixa. Devolve o cancelamento.
 */
export function startStationSync(): () => void {
  if (useStationStore.getState().visivel) void carregarEstacoes();
  return useStationStore.subscribe((a, b) => {
    if (a.visivel && !b.visivel) void carregarEstacoes();
  });
}

/**
 * Ponto amarelo no trilho quando o dado publicado está velho. O registro de
 * painéis (`ui/panels.ts`) já prevê esse aviso — é o mesmo que os focos usam.
 */
export function useEstacoesDesatualizadas(): boolean {
  const gerado = useStationStore((s) => s.dados.gerado);
  const visivel = useStationStore((s) => s.visivel);
  const erro = useStationStore((s) => s.situacao.estado === 'error');
  if (!visivel) return false;
  return erro || frescorDe(gerado) === 'atrasado' || frescorDe(gerado) === 'velho';
}

/** Só para os testes. */
export function resetStationSync(): void {
  emVoo?.abort();
  emVoo = null;
  jaCarregou = false;
}
